import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { reportsAPI, leadsAPI } from '@/services/api';
import { Users, DollarSign, TrendingUp, CheckCircle2 } from 'lucide-react';

const Dashboard = () => {
  const [report, setReport] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [reportData, leadsData] = await Promise.all([
        reportsAPI.getWeekly(),
        leadsAPI.getAll()
      ]);
      setReport(reportData);
      setRecentLeads(leadsData.slice(0, 5));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching dashboard data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="p-8">
        <p className="font-manrope text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-barlow text-4xl font-bold uppercase mb-2">Dashboard Overview</h1>
        <p className="font-manrope text-gray-600">Your weekly performance snapshot</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Total Leads</CardTitle>
            <Users className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-barlow font-bold">{report?.total_leads || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Qualified Leads</CardTitle>
            <TrendingUp className="h-5 w-5 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-barlow font-bold">{report?.qualified_leads || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Ready to quote</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Booked Jobs</CardTitle>
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-barlow font-bold">{report?.booked_jobs || 0}</div>
            <p className="text-xs text-gray-500 mt-1">Confirmed projects</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-manrope font-semibold text-gray-600">Est. Revenue</CardTitle>
            <DollarSign className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-barlow font-bold">${(report?.estimated_revenue || 0).toLocaleString()}</div>
            <p className="text-xs text-gray-500 mt-1">From booked jobs</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <p className="text-gray-500 font-manrope">No leads yet</p>
            ) : (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-sm">
                    <div>
                      <p className="font-manrope font-semibold">{lead.name}</p>
                      <p className="text-sm text-gray-600">{lead.city} • {lead.roof_type}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                      lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                      lead.status === 'qualified' ? 'bg-yellow-100 text-yellow-700' :
                      lead.status === 'booked' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {lead.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Top Lead Sources</CardTitle>
          </CardHeader>
          <CardContent>
            {!report || report.top_sources.length === 0 ? (
              <p className="text-gray-500 font-manrope">No data yet</p>
            ) : (
              <div className="space-y-3">
                {report.top_sources.map((item) => (
                  <div key={item.source} className="flex items-center justify-between">
                    <span className="font-manrope font-semibold capitalize">{item.source}</span>
                    <span className="text-2xl font-barlow font-bold text-primary">{item.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
